import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";

import { CloseChangePlanModal } from "../action";
import {
  useChangePlan,
  useGetChangePlanEstimate,
  useGetPlansForUpdate,
} from ".";

function useBreadcrumbStepManager(closeModal, coupon) {
  const [data, setData] = useState({});
  const plansData = useGetPlansForUpdate();
  const { estimate, estimateWithCoupon, ...summaryProps } =
    useGetChangePlanEstimate(coupon);
  const { isProcessing, changePlan } = useChangePlan();

  const steps = [
    {
      title: "Choose Your New Plan",
      action: async () => {
        return true;
      },
      renderLeftActions: false,
      actionText: "Next",
      props: {
        ...plansData,
        initialPlanId: data.selectedPlanId,
        onSelect: (selectedPlanId) => {
          setData((d) => {
            return { ...d, selectedPlanId };
          });
          estimate(selectedPlanId);
        },
      },
      disableButton: !data?.selectedPlanId,
      loadingButton: false,
      renderCancelButton: true,
    },
    {
      title: "Summary",
      action: async () => {
        const changePlanResp = await changePlan(
          summaryProps?.newCbPlanId,
          summaryProps?.coupon?.id,
          Boolean(data?.applyAtRenewal)
        );

        if (changePlanResp) {
          setData((d) => {
            return { ...d, ...changePlanResp };
          });
          return true;
        }
        return false;
      },
      renderLeftActions: true,
      actionText: "Change Plan",
      props: {
        ...summaryProps,
        coupon: {
          ...summaryProps.coupon,
          onCouponApplied: ({ name }) => {
            return estimateWithCoupon(data.selectedPlanId, name);
          },
          onCouponRemoved: () => {
            return estimateWithCoupon(data.selectedPlanId, null);
          },
        },
        onApplyAtRenewal: (applyAtRenewal) => {
          setData((d) => {
            return { ...d, applyAtRenewal };
          });
        },
      },
      disableButton: isProcessing || summaryProps.loading,
      loadingButton: isProcessing,
      renderCancelButton: true,
    },
    {
      title: "Confirmation",
      action: async () => {
        closeModal();
      },
      renderLeftActions: false,
      actionText: "OK",
      props: data,
      disableButton: false,
      loadingButton: false,
      renderCancelButton: false,
    },
  ];

  return {
    steps: steps.map(({ title }) => {
      return title;
    }),
    isProcessing: isProcessing,
    getActiveStepAttribute: (index) => {
      return steps[index];
    },
  };
}

function useChangePlanManager(coupon, onClose) {
  const dispatch = useDispatch();
  const closeModal = useCallback(() => {
    onClose();
    dispatch(CloseChangePlanModal());
  }, [dispatch, onClose]);

  const { steps, getActiveStepAttribute } = useBreadcrumbStepManager(
    closeModal,
    coupon
  );
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const activeStep = getActiveStepAttribute(activeStepIndex);

  return {
    activeStepIndex: activeStepIndex,
    steps: steps,
    activeStep: activeStep,
    next: async () => {
      const resp = await activeStep.action();

      if (resp)
        setActiveStepIndex((prev) => {
          return prev + 1;
        });
    },
    back: () => {
      setActiveStepIndex((prev) => {
        return prev - 1;
      });
    },
    close: closeModal,
  };
}

export default useChangePlanManager;
